import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
  Alert,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Divider,
} from '@mui/material';
import { Info, Save, Add, Delete } from '@mui/icons-material';
import { replaceSpacesWithUnderscores } from "../../../../lib/utils.es6";
import { toCents, fromCents } from "../../moneyUtils";
import ViewProductPolicyMakerChecks from "./ViewProductPolicyMakerChecks";
import AddEditProductPolicyMakerCheck from "./AddEditProductPolicyMakerCheck";

const GRANULARITY_MULTIPLIERS = {
  Day: 1,
  Week: 7,
  Month: 30,
  Year: 365,
};

const MAX_IMAGE_BYTES = 2 * 1024 * 1024; // 2MB — stored inline as base64 on the Product document

// Client-generated guid for a new policy_maker_checks entry, sent to the backend
// as its `guid` so the identity is stable across save/reload cycles (the backend
// stores whatever guid it's given — there's no server-side regeneration for this
// embedded array). Not used for anything security-sensitive.
function generateLocalGuid() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function(c) {
    var r = (Math.random() * 16) | 0;
    var v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// Pure helpers extracted for unit testing (this file has no component-level
// test harness — see RatingNodesTab.js for the same pattern). Exported so
// they can be imported directly by ProductForm.test.js without rendering
// the component.

// Backend requires both fields together or neither (a product with only one
// set silently disables the end-date feature on the consumer side), and
// cardinality must be a positive integer — reject both cases client-side
// instead of letting the API round-trip a 400. Returns an error message, or
// "" when the pair is valid.
export function validatePolicyContractTerm(cardinality, granularity) {
  var cardinalitySet = cardinality !== "";
  var granularitySet = !!granularity;
  if (cardinalitySet !== granularitySet) {
    return "Policy Contract Term Cardinality and Granularity must both be set, or both left unset.";
  }
  // Reject 0 explicitly: `parseInt("0") || undefined` would otherwise
  // silently drop a "0" cardinality to undefined in the payload, defeating
  // the both-or-neither guarantee just established above.
  if (cardinalitySet && parseInt(cardinality, 10) < 1) {
    return "Policy Contract Term Cardinality must be a whole number of 1 or more.";
  }
  return "";
}

// product-configuration rejects a hold that could never take effect; catch it
// here rather than on a 400. Returns an error message, or "" when valid.
export function validateHoldExcessBeyondCover(formData) {
  if (!formData.hold_excess_beyond_cover) {
    return "";
  }
  if (formData.policy_contract_term_cardinality === "") {
    return "Holding excess premium needs a Policy Contract Term.";
  }
  if (
    formData.enforced_billing_day !== "" ||
    formData.first_premium_billing_grace_months !== ""
  ) {
    return "Holding excess premium cannot be combined with an Enforced " +
      "Billing Day or a First Premium Billing Grace.";
  }
  return "";
}

// Returns only the keys that should be sent — mirrors every other optional
// cardinality/granularity pair in this form (cool_off_*, grace_period_*):
// an empty cardinality or granularity is simply omitted, never sent as "".
export function buildPolicyContractTermFields(cardinality, granularity) {
  var fields = {};
  if (cardinality !== "") {
    fields.policy_contract_term_cardinality = parseInt(cardinality, 10) || undefined;
  }
  if (granularity) {
    fields.policy_contract_term_granularity = granularity;
  }
  return fields;
}

// schedule_beneficiary_operation.granularity is enum-constrained but NOT
// required on the backend (src/entity/schedule.beneficiary.operation.ts) —
// an empty string fails Mongoose's enum validator, so send undefined
// (dropped by JSON.stringify) instead of "" when left on "Select".
// The group-scheme flag is sent only when it is on, or when the stored product already
// carries the field, so an untouched form never writes it and a backend without the
// field never sees it.
export function buildGroupSchemeField(formData, initialData) {
  if (formData.is_group_scheme || (initialData && initialData.is_group_scheme !== undefined)) {
    return { is_group_scheme: !!formData.is_group_scheme };
  }
  return {};
}

export function buildScheduleBeneficiaryOperationPayload(sbo) {
  if (!sbo || sbo.cardinality === "") {
    return undefined;
  }
  return {
    cardinality: parseInt(sbo.cardinality, 10) || undefined,
    granularity: sbo.granularity || undefined,
  };
}

// Same reasoning as buildScheduleBeneficiaryOperationPayload, for
// in_arrears_premium_granularity (src/entity/reinstatement.ts).
export function buildInArrearsReinstatementPayload(iar) {
  if (!iar || iar.in_arrears_premium_cardinality === "" || iar.in_arrears_premium_cardinality == null) {
    return undefined;
  }
  return {
    in_arrears_premium_cardinality: parseInt(iar.in_arrears_premium_cardinality, 10) || undefined,
    in_arrears_premium_granularity: iar.in_arrears_premium_granularity || undefined,
  };
}

const defaultFormData = {
  name: "",
  code: "",
  type: "",
  cover_type: "",
  currency: "",
  eligibility_threshold: "",
  tag: "",
  notes: "",
  minimum_age: "1",
  maximum_age: "200",
  waiting_period_days: "0",
  lapse_period_cardinality: "",
  lapse_period_granularity: "Day",
  cool_off_period_hours: "",
  cool_off_cardinality: "",
  cool_off_granularity: "",
  debit_grace_days: "",
  grace_period_cardinality: "",
  grace_period_granularity: "",
  policy_contract_term_cardinality: "",
  policy_contract_term_granularity: "",
  policy_number_prefix: "",
  enforced_start_date: "None",
  payment_effected_date_floor: "PolicyStartDate",
  reinstatement: {
    active: false,
    max_granularity: "",
    max_cardinality: "",
    catch_up_periods: "",
  },
  // ntu.granularity has no blank "Select" option below — unlike sbo/iar,
  // it's `required: true` on the backend (src/entity/ntu.ts), so a real
  // default is correct here, not a bug to "fix" into blank-optional.
  ntu: {
    cardinality: "",
    granularity: "Day",
  },
  schedule_beneficiary_operation: {
    cardinality: "",
    granularity: "",
  },
  enforced_billing_day: "",
  first_premium_billing_grace_months: "",
  in_arrears_reinstatement: {
    in_arrears_premium_cardinality: "",
    in_arrears_premium_granularity: "",
  },
  image: "",
  payment_maker_checker: false,
  claim_maker_checker: false,
  quote_maker_checker: false,
  refund_maker_checker: false,
  automatic_refund_maker_checker: false,
  block_manual_refund_on_claim: false,
  transfer_maker_checker: false,
  hold_excess_beyond_cover: false,
  is_group_scheme: false,
};

const ProductForm = ({ onSubmit, initialData, isEdit, currencyOptions }) => {
  const [formData, setFormData] = useState(defaultFormData);
  const [purchaseConstraints, setPurchaseConstraints] = useState([]);
  const [newConstraint, setNewConstraint] = useState({ benefits_str: "", max_total_benefits_amount: "" });
  const [policyMakerChecks, setPolicyMakerChecks] = useState([]);
  const [policyMakerCheckDialogOpen, setPolicyMakerCheckDialogOpen] = useState(false);
  const [editingPolicyMakerCheck, setEditingPolicyMakerCheck] = useState(null);
  const [imageError, setImageError] = useState("");
  const [policyContractTermError, setPolicyContractTermError] = useState("");
  const [holdExcessError, setHoldExcessError] = useState("");

  useEffect(() => {
    if (!initialData) {
      return;
    }

    // Handle reinstatement API typo: both `reinstatement` and `reinstament` exist in the wild
    var reinstatementRaw = initialData.reinstatement || initialData.reinstament || {};

    var constraintsInternal = [];
    if (initialData.purchase_constraints && Array.isArray(initialData.purchase_constraints)) {
      constraintsInternal = initialData.purchase_constraints.map(function(c) {
        return {
          benefits_str: c.benefits ? c.benefits.join(", ") : "",
          max_total_benefits_amount: c.max_total_benefits_amount ? fromCents(c.max_total_benefits_amount) : "",
        };
      });
    }

    var ntuRaw = initialData.ntu || {};
    var sboRaw = initialData.schedule_beneficiary_operation || {};
    var iarRaw = initialData.in_arrears_reinstatement || {};

    var policyMakerChecksInternal = [];
    if (initialData.policy_maker_checks && Array.isArray(initialData.policy_maker_checks)) {
      policyMakerChecksInternal = initialData.policy_maker_checks.map(function(c) {
        // Preserve the backend guid as the stable identity. Only entries that
        // somehow arrive without one (legacy data saved before this fix) get a
        // freshly generated guid here — and that guid is sent back on save, so
        // it stabilizes from that point on instead of being regenerated forever.
        return {
          _local_id: c.guid || generateLocalGuid(),
          type: c.type,
          label: c.label,
          sequence: c.sequence,
          allow_to_skip: !!c.allow_to_skip,
        };
      });
    }

    setFormData({
      name: initialData.name || "",
      code: initialData.code || "",
      type: initialData.type || "",
      cover_type: initialData.cover_type || "",
      currency: initialData.currency || "",
      eligibility_threshold: initialData.eligibility_threshold !== undefined && initialData.eligibility_threshold !== null
        ? String(initialData.eligibility_threshold) : "",
      tag: initialData.tag || "",
      notes: initialData.notes || "",
      minimum_age: initialData.minimum_age !== undefined && initialData.minimum_age !== null
        ? String(initialData.minimum_age) : "1",
      maximum_age: initialData.maximum_age !== undefined && initialData.maximum_age !== null
        ? String(initialData.maximum_age) : "200",
      waiting_period_days: initialData.waiting_period_days !== undefined && initialData.waiting_period_days !== null
        ? String(initialData.waiting_period_days) : "0",
      lapse_period_cardinality: initialData.lapse_period_cardinality !== undefined && initialData.lapse_period_cardinality !== null
        ? String(initialData.lapse_period_cardinality) : "",
      lapse_period_granularity: initialData.lapse_period_granularity || "Day",
      cool_off_period_hours: initialData.cool_off_period_hours !== undefined && initialData.cool_off_period_hours !== null
        ? String(initialData.cool_off_period_hours) : "",
      cool_off_cardinality: initialData.cool_off_cardinality !== undefined && initialData.cool_off_cardinality !== null
        ? String(initialData.cool_off_cardinality) : "",
      cool_off_granularity: initialData.cool_off_granularity || "",
      debit_grace_days: initialData.debit_grace_days !== undefined && initialData.debit_grace_days !== null
        ? String(initialData.debit_grace_days) : "",
      grace_period_cardinality: initialData.grace_period_cardinality !== undefined && initialData.grace_period_cardinality !== null
        ? String(initialData.grace_period_cardinality) : "",
      grace_period_granularity: initialData.grace_period_granularity || "",
      policy_contract_term_cardinality: initialData.policy_contract_term_cardinality !== undefined && initialData.policy_contract_term_cardinality !== null
        ? String(initialData.policy_contract_term_cardinality) : "",
      policy_contract_term_granularity: initialData.policy_contract_term_granularity || "",
      policy_number_prefix: initialData.policy_number_prefix || "",
      enforced_start_date: initialData.enforced_start_date || "None",
      payment_effected_date_floor: initialData.payment_effected_date_floor || "PolicyStartDate",
      reinstatement: {
        active: reinstatementRaw.active || false,
        max_cardinality: reinstatementRaw.max_cardinality !== undefined && reinstatementRaw.max_cardinality !== null
          ? String(reinstatementRaw.max_cardinality) : "",
        max_granularity: reinstatementRaw.max_granularity || "",
        catch_up_periods: reinstatementRaw.catch_up_periods !== undefined && reinstatementRaw.catch_up_periods !== null
          ? String(reinstatementRaw.catch_up_periods) : "",
      },
      ntu: {
        cardinality: ntuRaw.cardinality !== undefined && ntuRaw.cardinality !== null
          ? String(ntuRaw.cardinality) : "",
        granularity: ntuRaw.granularity || "Day",
      },
      schedule_beneficiary_operation: {
        cardinality: sboRaw.cardinality !== undefined && sboRaw.cardinality !== null
          ? String(sboRaw.cardinality) : "",
        granularity: sboRaw.granularity || "",
      },
      enforced_billing_day: initialData.enforced_billing_day !== undefined && initialData.enforced_billing_day !== null
        ? String(initialData.enforced_billing_day) : "",
      first_premium_billing_grace_months: initialData.first_premium_billing_grace_months !== undefined && initialData.first_premium_billing_grace_months !== null
        ? String(initialData.first_premium_billing_grace_months) : "",
      in_arrears_reinstatement: {
        in_arrears_premium_cardinality: iarRaw.in_arrears_premium_cardinality !== undefined && iarRaw.in_arrears_premium_cardinality !== null
          ? String(iarRaw.in_arrears_premium_cardinality) : "",
        in_arrears_premium_granularity: iarRaw.in_arrears_premium_granularity || "",
      },
      image: initialData.image || "",
      payment_maker_checker: !!initialData.payment_maker_checker,
      claim_maker_checker: !!initialData.claim_maker_checker,
      quote_maker_checker: !!initialData.quote_maker_checker,
      refund_maker_checker: !!initialData.refund_maker_checker,
      automatic_refund_maker_checker: !!initialData.automatic_refund_maker_checker,
      block_manual_refund_on_claim: !!initialData.block_manual_refund_on_claim,
      transfer_maker_checker: !!initialData.transfer_maker_checker,
      hold_excess_beyond_cover: !!initialData.hold_excess_beyond_cover,
      is_group_scheme: initialData.is_group_scheme === true,
    });

    setPurchaseConstraints(constraintsInternal);
    setPolicyMakerChecks(policyMakerChecksInternal);
  }, [initialData]);

  const handleInputChange = (field) => (event) => {
    var value = event.target.value;

    if (field === "policy_contract_term_cardinality" || field === "policy_contract_term_granularity") {
      setPolicyContractTermError("");
    }

    if (field === "name" && !isEdit) {
      var generatedCode = replaceSpacesWithUnderscores(event.target.value.toLowerCase());
      setFormData({ ...formData, name: event.target.value, code: generatedCode });
      return;
    }

    var rawFields = ['tag', 'notes', 'currency', 'policy_number_prefix'];
    if (rawFields.includes(field)) {
      setFormData({ ...formData, [field]: value });
      return;
    }

    var numericFields = [
      'lapse_period_cardinality',
      'cool_off_period_hours', 'cool_off_cardinality', 'debit_grace_days',
      'grace_period_cardinality', 'policy_contract_term_cardinality',
      'eligibility_threshold', 'minimum_age',
      'maximum_age', 'waiting_period_days',
      'enforced_billing_day', 'first_premium_billing_grace_months',
    ];

    if (numericFields.includes(field)) {
      if (value !== "" && (isNaN(value) || parseFloat(value) < 0)) {
        return;
      }
      setFormData({ ...formData, [field]: value });
      return;
    }

    setFormData({ ...formData, [field]: value });
  };

  const handleReinstatementChange = (field) => (event) => {
    var value = field === 'active' ? event.target.checked : event.target.value;
    if (field === 'max_cardinality' && value !== "" && (isNaN(value) || parseFloat(value) < 0)) {
      return;
    }
    var current = formData.reinstatement || {};
    setFormData({ ...formData, reinstatement: { ...current, [field]: value } });
  };

  const handleNtuChange = (field) => (event) => {
    var value = event.target.value;
    if (field === 'cardinality' && value !== "" && (isNaN(value) || parseFloat(value) < 0)) {
      return;
    }
    var current = formData.ntu || {};
    setFormData({ ...formData, ntu: { ...current, [field]: value } });
  };

  const handleSboChange = (field) => (event) => {
    var value = event.target.value;
    if (field === 'cardinality' && value !== "" && (isNaN(value) || parseFloat(value) < 0)) {
      return;
    }
    var current = formData.schedule_beneficiary_operation || {};
    setFormData({ ...formData, schedule_beneficiary_operation: { ...current, [field]: value } });
  };

  const handleInArrearsChange = (field) => (event) => {
    var value = event.target.value;
    if (field === 'in_arrears_premium_cardinality' && value !== "" && (isNaN(value) || parseFloat(value) < 0)) {
      return;
    }
    var current = formData.in_arrears_reinstatement || {};
    setFormData({ ...formData, in_arrears_reinstatement: { ...current, [field]: value } });
  };

  const handleConstraintChange = (index, field) => (event) => {
    var value = event.target.value;
    var updated = purchaseConstraints.map(function(c, i) {
      if (i === index) {
        return { ...c, [field]: value };
      }
      return c;
    });
    setPurchaseConstraints(updated);
  };

  const handleAddConstraint = () => {
    if (!newConstraint.benefits_str.trim()) {
      return;
    }
    setPurchaseConstraints([...purchaseConstraints, { ...newConstraint }]);
    setNewConstraint({ benefits_str: "", max_total_benefits_amount: "" });
  };

  const handleRemoveConstraint = (index) => {
    setPurchaseConstraints(purchaseConstraints.filter(function(_, i) { return i !== index; }));
  };

  const handleBooleanToggle = (field) => (event) => {
    setFormData({ ...formData, [field]: event.target.checked });
  };

  const handleImageChange = (event) => {
    var file = event.target.files && event.target.files[0];
    if (!file) {
      return;
    }
    if (!file.type || !file.type.startsWith("image/")) {
      setImageError("Please select an image file.");
      event.target.value = "";
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setImageError("Image must be " + (MAX_IMAGE_BYTES / (1024 * 1024)) + "MB or smaller.");
      event.target.value = "";
      return;
    }
    setImageError("");
    var reader = new FileReader();
    reader.onload = function() {
      // Functional updater: onload fires async, so don't clobber other field
      // edits (e.g. a checkbox toggled while the file was reading) with a stale
      // formData snapshot captured at pick time.
      setFormData(function(prev) { return { ...prev, image: reader.result }; });
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setImageError("");
    setFormData({ ...formData, image: "" });
  };

  const handleAddPolicyMakerCheckClick = () => {
    setEditingPolicyMakerCheck(null);
    setPolicyMakerCheckDialogOpen(true);
  };

  const handleEditPolicyMakerCheck = (check) => {
    setEditingPolicyMakerCheck(check);
    setPolicyMakerCheckDialogOpen(true);
  };

  const handleDeletePolicyMakerCheck = (check) => {
    setPolicyMakerChecks(
      policyMakerChecks.filter(function(c) { return c._local_id !== check._local_id; })
    );
  };

  const handleClosePolicyMakerCheckDialog = () => {
    setPolicyMakerCheckDialogOpen(false);
    setEditingPolicyMakerCheck(null);
  };

  const handleSavePolicyMakerCheck = (payload) => {
    if (payload._local_id) {
      setPolicyMakerChecks(
        policyMakerChecks.map(function(c) {
          return c._local_id === payload._local_id ? { ...payload } : c;
        })
      );
    } else {
      setPolicyMakerChecks([
        ...policyMakerChecks,
        { ...payload, _local_id: generateLocalGuid() },
      ]);
    }
    handleClosePolicyMakerCheckDialog();
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    var pctError = validatePolicyContractTerm(
      formData.policy_contract_term_cardinality,
      formData.policy_contract_term_granularity
    );
    if (pctError) {
      setPolicyContractTermError(pctError);
      return;
    }
    setPolicyContractTermError("");

    var holdError = validateHoldExcessBeyondCover(formData);
    if (holdError) {
      setHoldExcessError(holdError);
      return;
    }
    setHoldExcessError("");

    var derivedLapseDays = formData.lapse_period_cardinality && formData.lapse_period_granularity
      ? parseInt(formData.lapse_period_cardinality) * (GRANULARITY_MULTIPLIERS[formData.lapse_period_granularity] || 1)
      : undefined;

    var apiConstraints = purchaseConstraints.map(function(c) {
      var benefits = c.benefits_str.split(",").map(function(b) { return b.trim(); }).filter(function(b) { return b !== ""; });
      var result = { benefits: benefits };
      if (c.max_total_benefits_amount !== "" && c.max_total_benefits_amount !== null) {
        result.max_total_benefits_amount = toCents(c.max_total_benefits_amount);
      }
      return result;
    });

    var reinstatement = formData.reinstatement || {};
    var ntu = formData.ntu || {};
    var sbo = formData.schedule_beneficiary_operation || {};

    var payload = {
      name: formData.name,
      code: formData.code,
      type: formData.type,
      cover_type: formData.cover_type,
      enforced_start_date: formData.enforced_start_date,
      payment_effected_date_floor: formData.payment_effected_date_floor,
      lapse_period_granularity: formData.lapse_period_granularity,
      reinstatement: {
        active: reinstatement.active || false,
        max_cardinality: reinstatement.max_cardinality !== "" ? parseInt(reinstatement.max_cardinality) || undefined : undefined,
        max_granularity: reinstatement.max_granularity || undefined,
        catch_up_periods: reinstatement.catch_up_periods !== "" && reinstatement.catch_up_periods !== undefined
          ? parseInt(reinstatement.catch_up_periods) || undefined : undefined,
      },
    };

    if (formData.currency) { payload.currency = formData.currency; }
    if (formData.tag) { payload.tag = formData.tag; }
    if (formData.notes) { payload.notes = formData.notes; }
    if (formData.policy_number_prefix) { payload.policy_number_prefix = formData.policy_number_prefix; }

    if (formData.eligibility_threshold !== "") {
      payload.eligibility_threshold = parseInt(formData.eligibility_threshold) || undefined;
    }
    if (formData.minimum_age !== "") {
      payload.minimum_age = parseInt(formData.minimum_age) || 1;
    }
    if (formData.maximum_age !== "") {
      payload.maximum_age = parseInt(formData.maximum_age) || 200;
    }
    if (formData.waiting_period_days !== "") {
      payload.waiting_period_days = parseInt(formData.waiting_period_days) || 0;
    }
    if (formData.lapse_period_cardinality !== "") {
      payload.lapse_period_cardinality = parseInt(formData.lapse_period_cardinality) || undefined;
    }
    if (derivedLapseDays !== undefined) {
      payload.lapse_period_days = derivedLapseDays;
    }
    if (formData.cool_off_period_hours !== "") {
      payload.cool_off_period_hours = parseInt(formData.cool_off_period_hours) || undefined;
    }
    if (formData.cool_off_cardinality !== "") {
      payload.cool_off_cardinality = parseInt(formData.cool_off_cardinality) || undefined;
    }
    if (formData.cool_off_granularity) {
      payload.cool_off_granularity = formData.cool_off_granularity;
    }
    if (formData.debit_grace_days !== "") {
      payload.debit_grace_days = parseInt(formData.debit_grace_days) || undefined;
    }
    if (formData.grace_period_cardinality !== "") {
      payload.grace_period_cardinality = parseInt(formData.grace_period_cardinality) || undefined;
    }
    if (formData.grace_period_granularity) {
      payload.grace_period_granularity = formData.grace_period_granularity;
    }
    Object.assign(payload, buildPolicyContractTermFields(
      formData.policy_contract_term_cardinality,
      formData.policy_contract_term_granularity
    ));

    if (ntu.cardinality !== "") {
      payload.ntu = {
        cardinality: parseInt(ntu.cardinality) || undefined,
        granularity: ntu.granularity,
      };
    }

    var sboPayload = buildScheduleBeneficiaryOperationPayload(sbo);
    if (sboPayload) {
      payload.schedule_beneficiary_operation = sboPayload;
    }

    if (apiConstraints.length > 0) {
      payload.purchase_constraints = apiConstraints;
    }

    if (formData.enforced_billing_day !== "") {
      payload.enforced_billing_day = parseInt(formData.enforced_billing_day) || undefined;
    }
    if (formData.first_premium_billing_grace_months !== "") {
      payload.first_premium_billing_grace_months = parseInt(formData.first_premium_billing_grace_months) || undefined;
    }

    var iar = formData.in_arrears_reinstatement || {};
    var iarPayload = buildInArrearsReinstatementPayload(iar);
    if (iarPayload) {
      payload.in_arrears_reinstatement = iarPayload;
    }

    // Always send image (including "" when cleared) so removing a logo persists
    // rather than being preserved by a partial-update backend.
    payload.image = formData.image || "";
    payload.payment_maker_checker = !!formData.payment_maker_checker;
    payload.claim_maker_checker = !!formData.claim_maker_checker;
    payload.quote_maker_checker = !!formData.quote_maker_checker;
    payload.refund_maker_checker = !!formData.refund_maker_checker;
    payload.automatic_refund_maker_checker = !!formData.automatic_refund_maker_checker;
    payload.block_manual_refund_on_claim = !!formData.block_manual_refund_on_claim;
    payload.transfer_maker_checker = !!formData.transfer_maker_checker;
    payload.hold_excess_beyond_cover = !!formData.hold_excess_beyond_cover;
    Object.assign(payload, buildGroupSchemeField(formData, initialData));
    payload.policy_maker_checks = policyMakerChecks.map(function(c) {
      return {
        // _local_id IS the guid (backend-original if this entry came from
        // initialData, freshly client-generated otherwise) — sending it back
        // keeps identity stable across save/reload instead of losing it.
        guid: c._local_id,
        type: c.type,
        label: c.label,
        sequence: c.sequence,
        allow_to_skip: !!c.allow_to_skip,
      };
    });

    if (onSubmit) {
      onSubmit(payload);
    }
  };

  var derivedLapseDays = formData.lapse_period_cardinality && formData.lapse_period_granularity
    ? parseInt(formData.lapse_period_cardinality) * (GRANULARITY_MULTIPLIERS[formData.lapse_period_granularity] || 1)
    : "";

  var reinstatement = formData.reinstatement || {};
  var ntu = formData.ntu || {};
  var sbo = formData.schedule_beneficiary_operation || {};
  var iar = formData.in_arrears_reinstatement || {};

  // Options come from the backend country list (see currencyOptions prop).
  // If the current value isn't among them — a legacy product, or an
  // AI-drafted value that isn't a configured country currency — append it
  // so the field still shows and saves the real value instead of going blank.
  var baseCurrencyOptions = currencyOptions || [];
  var currentCurrency = formData.currency;
  var displayedCurrencyOptions = useMemo(function() {
    var isKnown = !currentCurrency || baseCurrencyOptions.some(function(opt) {
      return opt.code === currentCurrency;
    });
    if (isKnown) {
      return baseCurrencyOptions;
    }
    return baseCurrencyOptions.concat([{
      code: currentCurrency,
      label: currentCurrency + " (not a configured country currency)",
    }]);
  }, [baseCurrencyOptions, currentCurrency]);

  return (
    <>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: "#173E4B" }}>
        Basic Information
      </Typography>

      {/* Product Name and Code Row */}
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3, mb: 3 }}>
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
            Product Name
          </Typography>
          <TextField
            fullWidth
            value={formData.name}
            onChange={handleInputChange("name")}
            variant="outlined"
            sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "#fafafa" } }}
          />
        </Box>
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
            Product Code
          </Typography>
          <TextField
            disabled
            fullWidth
            value={formData.code}
            variant="outlined"
            sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "#fafafa" } }}
          />
          <FormHelperText sx={{ ml: 0, mt: 1 }}>Use snake_case for product codes</FormHelperText>
        </Box>
      </Box>

      {/* Product Type, Cover Type, Currency Row */}
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 3, mb: 3 }}>
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
            Product Type
          </Typography>
          <FormControl fullWidth>
            <Select
              value={formData.type}
              onChange={handleInputChange("type")}
              sx={{ backgroundColor: "#fafafa" }}
            >
              <MenuItem value="Paid">Paid</MenuItem>
              <MenuItem value="Free">Free</MenuItem>
              <MenuItem value="Paid_Bundled">Paid Bundled</MenuItem>
              <MenuItem value="Free_Agent">Free Agent</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
            Cover Type
          </Typography>
          <FormControl fullWidth>
            <Select
              value={formData.cover_type}
              onChange={handleInputChange("cover_type")}
              sx={{ backgroundColor: "#fafafa" }}
            >
              <MenuItem value="Individual">Individual</MenuItem>
              <MenuItem value="Family">Family</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
            Currency
          </Typography>
          <FormControl fullWidth>
            <Select
              value={formData.currency}
              onChange={handleInputChange("currency")}
              displayEmpty
              sx={{ backgroundColor: "#fafafa" }}
            >
              <MenuItem value="">None</MenuItem>
              {displayedCurrencyOptions.map(function(opt) {
                return <MenuItem key={opt.code} value={opt.code}>{opt.label}</MenuItem>;
              })}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Tag, Notes Row */}
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3, mb: 3 }}>
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
            Tag
          </Typography>
          <TextField
            fullWidth
            placeholder="e.g. funeral, credit-life"
            value={formData.tag}
            onChange={handleInputChange("tag")}
            variant="outlined"
            sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "#fafafa" } }}
          />
        </Box>
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
            Notes
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={2}
            placeholder="Optional notes about this product"
            value={formData.notes}
            onChange={handleInputChange("notes")}
            variant="outlined"
            sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "#fafafa" } }}
          />
        </Box>
      </Box>

      {/* Eligibility & Periods */}
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, mt: 1, color: "#173E4B" }}>
        Eligibility & Periods
      </Typography>
      {/* Age Range, Eligibility Threshold, Waiting Period Row */}
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 3, mb: 4 }}>
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
            Minimum Age
          </Typography>
          <TextField
            fullWidth
            type="number"
            value={formData.minimum_age}
            onChange={handleInputChange("minimum_age")}
            variant="outlined"
            sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "#fafafa" } }}
          />
        </Box>
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
            Maximum Age
          </Typography>
          <TextField
            fullWidth
            type="number"
            value={formData.maximum_age}
            onChange={handleInputChange("maximum_age")}
            variant="outlined"
            sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "#fafafa" } }}
          />
        </Box>
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
            Eligibility Threshold
          </Typography>
          <TextField
            fullWidth
            type="number"
            placeholder="Optional"
            value={formData.eligibility_threshold}
            onChange={handleInputChange("eligibility_threshold")}
            variant="outlined"
            sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "#fafafa" } }}
          />
        </Box>
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
            Waiting Period (days)
          </Typography>
          <TextField
            fullWidth
            type="number"
            value={formData.waiting_period_days}
            onChange={handleInputChange("waiting_period_days")}
            variant="outlined"
            sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "#fafafa" } }}
          />
        </Box>
      </Box>

      {/* Lapse Period Configuration Section */}
      <Box
        sx={{
          border: "1px solid #F8F9FA",
          borderRadius: 2,
          p: 3,
          backgroundColor: "#fef9f3",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <Info sx={{ fontSize: 20, color: "#173E4B" }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Lapse Period Configuration
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Define how long before a policy lapses due to non-payment
        </Typography>

        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3, mb: 3 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              Lapse Period Cardinality
            </Typography>
            <TextField
              fullWidth
              type="number"
              value={formData.lapse_period_cardinality}
              onChange={handleInputChange("lapse_period_cardinality")}
              variant="outlined"
              sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "white" } }}
            />
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              Lapse Period Granularity
            </Typography>
            <FormControl fullWidth>
              <Select
                value={formData.lapse_period_granularity}
                onChange={handleInputChange("lapse_period_granularity")}
                sx={{ backgroundColor: "white" }}
              >
                <MenuItem value="Day">Day</MenuItem>
                <MenuItem value="Week">Week</MenuItem>
                <MenuItem value="Month">Month</MenuItem>
                <MenuItem value="Year">Year</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Box>

      {/* Policy Configuration Section */}
      <Box
        sx={{
          border: "1px solid #F8F9FA",
          borderRadius: 2,
          p: 3,
          backgroundColor: "#fef9f3",
          mb: 3,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
          Policy Configuration
        </Typography>

        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3, mb: 1 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              Policy Contract Term Cardinality
            </Typography>
            <TextField
              fullWidth
              type="number"
              value={formData.policy_contract_term_cardinality}
              onChange={handleInputChange("policy_contract_term_cardinality")}
              variant="outlined"
              placeholder="Number of units"
              sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "white" } }}
            />
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              Policy Contract Term Granularity
            </Typography>
            <FormControl fullWidth>
              <Select
                value={formData.policy_contract_term_granularity}
                onChange={handleInputChange("policy_contract_term_granularity")}
                sx={{ backgroundColor: "white" }}
              >
                <MenuItem value="">Select</MenuItem>
                <MenuItem value="Day">Day</MenuItem>
                <MenuItem value="Week">Week</MenuItem>
                <MenuItem value="Month">Month</MenuItem>
                <MenuItem value="Year">Year</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
        <Box sx={{ mb: 3 }}>
          <FormHelperText sx={{ ml: 0 }}>
            The fixed cover term for a policy, computed from its start date. Must be set together, or both left unset.
          </FormHelperText>
          {policyContractTermError && (
            <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>
              {policyContractTermError}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3, mb: 3 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              Cool-off Period Hours
            </Typography>
            <TextField
              fullWidth
              type="number"
              value={formData.cool_off_period_hours}
              onChange={handleInputChange("cool_off_period_hours")}
              variant="outlined"
              placeholder="Maximum hours to cancel policy for refund"
              sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "white" } }}
            />
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              Cool-off Cardinality
            </Typography>
            <TextField
              fullWidth
              type="number"
              value={formData.cool_off_cardinality}
              onChange={handleInputChange("cool_off_cardinality")}
              variant="outlined"
              sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "white" } }}
            />
          </Box>
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3, mb: 3 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              Cool-off Granularity
            </Typography>
            <FormControl fullWidth>
              <Select
                value={formData.cool_off_granularity}
                onChange={handleInputChange("cool_off_granularity")}
                sx={{ backgroundColor: "white" }}
              >
                <MenuItem value="">Select</MenuItem>
                <MenuItem value="Day">Day</MenuItem>
                <MenuItem value="Week">Week</MenuItem>
                <MenuItem value="Month">Month</MenuItem>
                <MenuItem value="Year">Year</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              Debit Grace Days
            </Typography>
            <TextField
              fullWidth
              type="number"
              value={formData.debit_grace_days}
              onChange={handleInputChange("debit_grace_days")}
              variant="outlined"
              placeholder="Days after due date for valid claims"
              sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "white" } }}
            />
          </Box>
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3, mb: 3 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              Grace Period Cardinality
            </Typography>
            <TextField
              fullWidth
              type="number"
              value={formData.grace_period_cardinality}
              onChange={handleInputChange("grace_period_cardinality")}
              variant="outlined"
              sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "white" } }}
            />
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              Grace Period Granularity
            </Typography>
            <FormControl fullWidth>
              <Select
                value={formData.grace_period_granularity}
                onChange={handleInputChange("grace_period_granularity")}
                sx={{ backgroundColor: "white" }}
              >
                <MenuItem value="">Select</MenuItem>
                <MenuItem value="Day">Day</MenuItem>
                <MenuItem value="Week">Week</MenuItem>
                <MenuItem value="Month">Month</MenuItem>
                <MenuItem value="Year">Year</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
            Policy Number Prefix
          </Typography>
          <TextField
            fullWidth
            value={formData.policy_number_prefix}
            onChange={handleInputChange("policy_number_prefix")}
            variant="outlined"
            placeholder="Prefix for policy numbers (e.g., POL-)"
            sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "white" } }}
          />
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
            Enforced Start Date
          </Typography>
          <FormControl fullWidth>
            <Select
              value={formData.enforced_start_date}
              onChange={handleInputChange("enforced_start_date")}
              sx={{ backgroundColor: "white" }}
            >
              <MenuItem value="None">None</MenuItem>
              <MenuItem value="FirstDayOfMonth">First Day of Month</MenuItem>
              <MenuItem value="FirstDayOfNextMonth">First Day of Next Month</MenuItem>
            </Select>
          </FormControl>
          <FormHelperText sx={{ ml: 0, mt: 1 }}>
            Controls when new policies start: None (immediate), FirstDayOfMonth (current month), or FirstDayOfNextMonth
          </FormHelperText>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
            Payment Effected Date Floor
          </Typography>
          <FormControl fullWidth>
            <Select
              value={formData.payment_effected_date_floor}
              onChange={handleInputChange("payment_effected_date_floor")}
              sx={{ backgroundColor: "white" }}
            >
              <MenuItem value="None">None</MenuItem>
              <MenuItem value="PolicyStartDate">Policy Start Date</MenuItem>
              <MenuItem value="PolicyCreatedDate">Policy Created Date</MenuItem>
            </Select>
          </FormControl>
          <FormHelperText sx={{ ml: 0, mt: 1 }}>
            Earliest date a payment's effected_at may be: None (no check), PolicyStartDate (default), or PolicyCreatedDate (for partners with a migrated book of business, where created_at is the migration date rather than the true start_date)
          </FormHelperText>
        </Box>
      </Box>

      {/* Reinstatement Section */}
      <Box
        sx={{
          border: "1px solid #F8F9FA",
          borderRadius: 2,
          p: 3,
          backgroundColor: "#fef9f3",
          mb: 3,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Reinstatement
        </Typography>

        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={reinstatement.active || false}
                onChange={handleReinstatementChange("active")}
                sx={{ color: "#173E4B", "&.Mui-checked": { color: "#173E4B" } }}
              />
            }
            label="Reinstatement Active"
          />
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              Max Cardinality
            </Typography>
            <TextField
              fullWidth
              type="number"
              value={reinstatement.max_cardinality || ""}
              onChange={handleReinstatementChange("max_cardinality")}
              variant="outlined"
              placeholder="e.g. 3"
              sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "white" } }}
            />
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              Max Granularity
            </Typography>
            <FormControl fullWidth>
              <Select
                value={reinstatement.max_granularity || ""}
                onChange={handleReinstatementChange("max_granularity")}
                sx={{ backgroundColor: "white" }}
              >
                <MenuItem value="">Select</MenuItem>
                <MenuItem value="Day">Day</MenuItem>
                <MenuItem value="Week">Week</MenuItem>
                <MenuItem value="Month">Month</MenuItem>
                <MenuItem value="Year">Year</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
            Periods still owed when coverage is restarted (optional)
          </Typography>
          <TextField
            fullWidth
            type="number"
            inputProps={{ min: 1 }}
            value={reinstatement.catch_up_periods || ""}
            onChange={handleReinstatementChange("catch_up_periods")}
            variant="outlined"
            placeholder="e.g. 2"
            helperText="A policy more than this many premium periods behind has its coverage restarted when a payment arrives: the current quotation ends and a new one starts this many periods ago, so only these periods are owed. Leave empty to keep the whole arrears owing."
            sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "white" } }}
          />
        </Box>
      </Box>

      {/* NTU Section */}
      <Box
        sx={{
          border: "1px solid #F8F9FA",
          borderRadius: 2,
          p: 3,
          backgroundColor: "#fef9f3",
          mb: 3,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          NTU (Not Taken Up)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Period after which an unpaid policy is marked as not taken up
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              Cardinality
            </Typography>
            <TextField
              fullWidth
              type="number"
              value={ntu.cardinality || ""}
              onChange={handleNtuChange("cardinality")}
              variant="outlined"
              sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "white" } }}
            />
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              Granularity
            </Typography>
            <FormControl fullWidth>
              <Select
                value={ntu.granularity || "Day"}
                onChange={handleNtuChange("granularity")}
                sx={{ backgroundColor: "white" }}
              >
                <MenuItem value="Day">Day</MenuItem>
                <MenuItem value="Week">Week</MenuItem>
                <MenuItem value="Month">Month</MenuItem>
                <MenuItem value="Year">Year</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Box>

      {/* Schedule Beneficiary Operation Section */}
      <Box
        sx={{
          border: "1px solid #F8F9FA",
          borderRadius: 2,
          p: 3,
          backgroundColor: "#fef9f3",
          mb: 3,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          Schedule Beneficiary Operation
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          How often beneficiary records are scheduled for processing
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              Cardinality
            </Typography>
            <TextField
              fullWidth
              type="number"
              value={sbo.cardinality || ""}
              onChange={handleSboChange("cardinality")}
              variant="outlined"
              sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "white" } }}
            />
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              Granularity
            </Typography>
            <FormControl fullWidth>
              <Select
                value={sbo.granularity || ""}
                onChange={handleSboChange("granularity")}
                sx={{ backgroundColor: "white" }}
              >
                <MenuItem value="">Select</MenuItem>
                <MenuItem value="Day">Day</MenuItem>
                <MenuItem value="Week">Week</MenuItem>
                <MenuItem value="Month">Month</MenuItem>
                <MenuItem value="Year">Year</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Box>

      {/* Billing & Arrears Section */}
      <Box
        sx={{
          border: "1px solid #F8F9FA",
          borderRadius: 2,
          p: 3,
          backgroundColor: "#fef9f3",
          mb: 3,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          Billing & Arrears
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Billing day, first-premium grace, and in-arrears reinstatement window
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3, mb: 3 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              Enforced Billing Day
            </Typography>
            <TextField
              fullWidth
              type="number"
              placeholder="Day of month (1–31)"
              value={formData.enforced_billing_day}
              onChange={handleInputChange("enforced_billing_day")}
              variant="outlined"
              sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "white" } }}
            />
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              First Premium Billing Grace (months)
            </Typography>
            <TextField
              fullWidth
              type="number"
              placeholder="Optional"
              value={formData.first_premium_billing_grace_months}
              onChange={handleInputChange("first_premium_billing_grace_months")}
              variant="outlined"
              sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "white" } }}
            />
          </Box>
        </Box>
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
            Hold Premium Paid Beyond Cover End
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
            Money paid past the end of cover is held, then applied on renewal
            or released. Needs a Policy Contract Term; cannot be combined with
            a billing day or first premium grace.
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={!!formData.hold_excess_beyond_cover}
                onChange={handleBooleanToggle("hold_excess_beyond_cover")}
                sx={{ color: "#173E4B", "&.Mui-checked": { color: "#173E4B" } }}
              />
            }
            label={formData.hold_excess_beyond_cover ? "Enabled" : "Disabled"}
          />
          {holdExcessError && (
            <Typography variant="caption" color="error" sx={{ display: "block" }}>
              {holdExcessError}
            </Typography>
          )}
        </Box>
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
            Group Scheme Product
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
            Policy administration copies this onto each policy when it is bought
            and uses it to restrict who may change the policy. Turning it on
            does not flag policies that already exist; they need a backfill by
            a developer.
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={!!formData.is_group_scheme}
                onChange={handleBooleanToggle("is_group_scheme")}
                sx={{ color: "#173E4B", "&.Mui-checked": { color: "#173E4B" } }}
              />
            }
            label={formData.is_group_scheme ? "Enabled" : "Disabled"}
          />
        </Box>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
          In-Arrears Reinstatement Window
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3 }}>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
              Cardinality
            </Typography>
            <TextField
              fullWidth
              type="number"
              value={iar.in_arrears_premium_cardinality || ""}
              onChange={handleInArrearsChange("in_arrears_premium_cardinality")}
              variant="outlined"
              sx={{ "& .MuiOutlinedInput-root": { backgroundColor: "white" } }}
            />
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
              Granularity
            </Typography>
            <FormControl fullWidth>
              <Select
                value={iar.in_arrears_premium_granularity || ""}
                onChange={handleInArrearsChange("in_arrears_premium_granularity")}
                sx={{ backgroundColor: "white" }}
              >
                <MenuItem value="">Select</MenuItem>
                <MenuItem value="Day">Day</MenuItem>
                <MenuItem value="Week">Week</MenuItem>
                <MenuItem value="Month">Month</MenuItem>
                <MenuItem value="Year">Year</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Box>

      {/* Purchase Constraints Section */}
      <Box
        sx={{
          border: "1px solid #F8F9FA",
          borderRadius: 2,
          p: 3,
          backgroundColor: "#fef9f3",
          mb: 3,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          Purchase Constraints
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Limit which benefits can be purchased together and set maximum amounts
        </Typography>

        {purchaseConstraints.length > 0 && (
          <Box sx={{ mb: 3 }}>
            {purchaseConstraints.map(function(constraint, index) {
              return (
                <Box
                  key={index}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr auto",
                    gap: 2,
                    mb: 2,
                    p: 2,
                    backgroundColor: "white",
                    borderRadius: 1,
                    border: "1px solid #e5e7eb",
                  }}
                >
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Benefits (comma-separated codes)
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      value={constraint.benefits_str}
                      onChange={handleConstraintChange(index, "benefits_str")}
                      variant="outlined"
                      placeholder="benefit_code_1, benefit_code_2"
                    />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Max Total Benefits Amount{formData.currency ? ' (' + formData.currency + ')' : ''}
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      value={constraint.max_total_benefits_amount}
                      onChange={handleConstraintChange(index, "max_total_benefits_amount")}
                      variant="outlined"
                      placeholder="0.00"
                    />
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "flex-end", pb: 0.5 }}>
                    <IconButton
                      size="small"
                      onClick={function() { handleRemoveConstraint(index); }}
                      sx={{ color: "#ef4444" }}
                    >
                      <Delete sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}

        <Divider sx={{ mb: 2 }} />

        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
          Add New Constraint
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 2, mb: 1 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Benefits (comma-separated codes)
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={newConstraint.benefits_str}
              onChange={function(e) { setNewConstraint({ ...newConstraint, benefits_str: e.target.value }); }}
              variant="outlined"
              placeholder="benefit_code_1, benefit_code_2"
            />
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Max Total Benefits Amount{formData.currency ? ' (' + formData.currency + ')' : ''}
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="number"
              value={newConstraint.max_total_benefits_amount}
              onChange={function(e) { setNewConstraint({ ...newConstraint, max_total_benefits_amount: e.target.value }); }}
              variant="outlined"
              placeholder="0.00"
            />
          </Box>
          <Box sx={{ display: "flex", alignItems: "flex-end", pb: 0.5 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Add />}
              onClick={handleAddConstraint}
              sx={{
                textTransform: "none",
                borderColor: "#173E4B",
                color: "#173E4B",
                "&:hover": { backgroundColor: "#f0f9ff" },
                whiteSpace: "nowrap",
              }}
            >
              Add
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Maker-Checker & Branding Section */}
      <Box
        sx={{
          border: "1px solid #F8F9FA",
          borderRadius: 2,
          p: 3,
          backgroundColor: "#fef9f3",
          mb: 3,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          Maker-Checker & Branding
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Product-level maker-checker overrides and logo. When set, the payment,
          claim and quote settings take precedence over the partner-level
          equivalents. Premium refund and transfer approval are set only here.
        </Typography>

        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 3, mb: 3 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              Payment Maker-Checker
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!formData.payment_maker_checker}
                  onChange={handleBooleanToggle("payment_maker_checker")}
                  sx={{ color: "#173E4B", "&.Mui-checked": { color: "#173E4B" } }}
                />
              }
              label={formData.payment_maker_checker ? "Enabled" : "Disabled"}
            />
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              Claim Maker-Checker
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!formData.claim_maker_checker}
                  onChange={handleBooleanToggle("claim_maker_checker")}
                  sx={{ color: "#173E4B", "&.Mui-checked": { color: "#173E4B" } }}
                />
              }
              label={formData.claim_maker_checker ? "Enabled" : "Disabled"}
            />
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              Quote Maker-Checker
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!formData.quote_maker_checker}
                  onChange={handleBooleanToggle("quote_maker_checker")}
                  sx={{ color: "#173E4B", "&.Mui-checked": { color: "#173E4B" } }}
                />
              }
              label={formData.quote_maker_checker ? "Enabled" : "Disabled"}
            />
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              Premium Refund Maker-Checker
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!formData.refund_maker_checker}
                  onChange={handleBooleanToggle("refund_maker_checker")}
                  sx={{ color: "#173E4B", "&.Mui-checked": { color: "#173E4B" } }}
                />
              }
              label={formData.refund_maker_checker ? "Enabled" : "Disabled"}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              Refunds, held-premium releases and automatic cool-off refunds wait for a second person.
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              System Refund Maker-Checker
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!formData.automatic_refund_maker_checker}
                  onChange={handleBooleanToggle("automatic_refund_maker_checker")}
                  sx={{ color: "#173E4B", "&.Mui-checked": { color: "#173E4B" } }}
                />
              }
              label={formData.automatic_refund_maker_checker ? "Enabled" : "Disabled"}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              Only refunds the system raises (cool-off cancellation, rejection) wait for a second person; refunds a person makes go straight through.
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              Block Refunds With Claims
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!formData.block_manual_refund_on_claim}
                  onChange={handleBooleanToggle("block_manual_refund_on_claim")}
                  sx={{ color: "#173E4B", "&.Mui-checked": { color: "#173E4B" } }}
                />
              }
              label={formData.block_manual_refund_on_claim ? "Enabled" : "Disabled"}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              A premium cannot be refunded by hand while a claim stands in the period it paid for.
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
              Premium Transfer Maker-Checker
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!formData.transfer_maker_checker}
                  onChange={handleBooleanToggle("transfer_maker_checker")}
                  sx={{ color: "#173E4B", "&.Mui-checked": { color: "#173E4B" } }}
                />
              }
              label={formData.transfer_maker_checker ? "Enabled" : "Disabled"}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              Transfers between policies wait for a second person.
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Box sx={{ mb: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
            Product Logo
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {formData.image && (
              <img
                src={formData.image}
                alt="Product logo preview"
                style={{ height: 56, width: 56, objectFit: "contain", border: "1px solid #e5e7eb", borderRadius: 4, background: "white" }}
              />
            )}
            <Button
              variant="outlined"
              component="label"
              size="small"
              sx={{ textTransform: "none", borderColor: "#173E4B", color: "#173E4B" }}
            >
              {formData.image ? "Replace Logo" : "Upload Logo"}
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={handleImageChange}
              />
            </Button>
            {formData.image && (
              <IconButton size="small" onClick={handleRemoveImage} sx={{ color: "#ef4444" }}>
                <Delete sx={{ fontSize: 18 }} />
              </IconButton>
            )}
          </Box>
          {imageError && (
            <Typography variant="caption" color="error" sx={{ display: "block", mt: 1 }}>
              {imageError}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Policy Maker-Checks Section */}
      <Box
        sx={{
          border: "1px solid #F8F9FA",
          borderRadius: 2,
          p: 3,
          backgroundColor: "#fef9f3",
          mb: 3,
        }}
      >
        <ViewProductPolicyMakerChecks
          policyMakerChecks={policyMakerChecks}
          onAddCheck={handleAddPolicyMakerCheckClick}
          onEditCheck={handleEditPolicyMakerCheck}
          onDeleteCheck={handleDeletePolicyMakerCheck}
        />
      </Box>

      <AddEditProductPolicyMakerCheck
        open={policyMakerCheckDialogOpen}
        existingCheck={editingPolicyMakerCheck}
        existingChecks={policyMakerChecks}
        onClose={handleClosePolicyMakerCheckDialog}
        onSave={handleSavePolicyMakerCheck}
      />

      {/* Summary Alert */}
      <Alert
        severity="info"
        sx={{
          mb: 3,
          backgroundColor: "#F8F9FA",
          color: "#b45309",
          "& .MuiAlert-icon": { color: "#173E4B" },
        }}
      >
        <Typography variant="body2">
          <strong>Configuration Summary:</strong> Lapse after{" "}
          {derivedLapseDays !== "" ? derivedLapseDays + " days" : "(not set)"}.
          {formData.cool_off_period_hours ? " Cool-off: " + formData.cool_off_period_hours + " hours." : ""}
          {formData.debit_grace_days ? " Grace: " + formData.debit_grace_days + " days." : ""}
          {formData.policy_contract_term_cardinality && formData.policy_contract_term_granularity
            ? " Contract term: " + formData.policy_contract_term_cardinality + " " + formData.policy_contract_term_granularity + "(s)."
            : ""}
          {formData.policy_number_prefix ? ' Prefix: "' + formData.policy_number_prefix + '".' : ""}
          {formData.currency ? " Currency: " + formData.currency + "." : ""}
        </Typography>
      </Alert>

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          startIcon={<Save />}
          onClick={handleSubmit}
          sx={{
            textTransform: "none",
            backgroundColor: "#fba92c",
            "&:hover": { backgroundColor: "#f59e0b" },
            px: 3,
            py: 1,
          }}
        >
          {isEdit ? "Update Product" : "Save Product"}
        </Button>
      </Box>
    </>
  );
};

export default ProductForm;
